import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const TestimonialsSection = () => {
  const testimonials = [
    {
      id: 1,
      name: "Sarah Chen",
      role: "CTO, TechFlow Solutions",
      company: "San Francisco, USA",
      avatar: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=150&h=150&fit=crop&crop=faces",
      content: `Lahiru's communication skills are exceptional. Despite the time zone difference, he kept us updated throughout the project and delivered exactly what we needed. His technical expertise combined with his professionalism made our collaboration seamless.`,
      rating: 5,
      project: "E-commerce Platform",
      highlight: "Excellent Communication"
    },
    {
      id: 2,
      name: "Marcus Johnson",
      role: "Product Manager, InnovateLab",
      company: "London, UK",
      avatar: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=150&h=150&fit=crop&crop=faces",
      content: `Working with Lahiru was a game-changer for our startup. He not only delivered high-quality code but also provided valuable insights that improved our product. His reliability and quick response times made him feel like part of our team.`,
      rating: 5,
      project: "SaaS Dashboard",
      highlight: "Reliable & Insightful"
    },
    {
      id: 3,
      name: "Emily Rodriguez",
      role: "Founder, GreenTech Innovations",
      company: "Toronto, Canada",
      avatar: "https://images.unsplash.com/photo-1580489944761-15a19d654956?w=150&h=150&fit=crop&crop=faces",
      content: `Lahiru's ability to understand complex requirements and translate them into elegant solutions is remarkable. He's always available for questions and provides clear explanations. I highly recommend him for any full-stack development project.`,
      rating: 5,
      project: "Environmental Monitoring App",
      highlight: "Problem Solver"
    }
  ];

  const communicationStats = [
    {
      icon: 'MessageSquare',
      label: 'Response Time',
      value: '< 24 hours',
      description: 'Average email response'
    },
    {
      icon: 'Users',
      label: 'Client Satisfaction',
      value: '100%',
      description: 'Positive feedback rate'
    },
    {
      icon: 'Globe',
      label: 'Time Zones',
      value: '15+',
      description: 'Successfully collaborated'
    },
    {
      icon: 'CheckCircle',
      label: 'Projects Delivered',
      value: '50+',
      description: 'On time & on budget'
    }
  ];

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, index) => (
      <Icon
        key={index}
        name="Star"
        size={16}
        className={index < rating ? 'text-warning fill-current' : 'text-muted'}
      />
    ));
  };

  return (
    <section className="py-20 bg-surface">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl lg:text-4xl font-bold text-text-primary mb-4">
            What Clients Say About
            <span className="gradient-text block">Working With Me</span>
          </h2>
          <p className="text-lg text-text-secondary max-w-2xl mx-auto">
            Communication and reliability are at the heart of successful remote collaboration
          </p>
        </div>

        {/* Communication Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {communicationStats.map((stat, index) => (
            <div key={index} className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-white rounded-xl shadow-md mb-4">
                <Icon name={stat.icon} size={28} className="text-primary" />
              </div>
              <div className="text-2xl font-bold text-text-primary mb-1">{stat.value}</div>
              <div className="text-sm font-medium text-text-primary mb-1">{stat.label}</div>
              <div className="text-xs text-text-muted">{stat.description}</div>
            </div>
          ))}
        </div>

        {/* Testimonials */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {testimonials.map((testimonial) => (
            <div
              key={testimonial.id}
              className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-premium hover-lift"
            >
              {/* Header */}
              <div className="flex items-center space-x-4 mb-6">
                <div className="relative">
                  <Image
                    src={testimonial.avatar}
                    alt={testimonial.name}
                    className="w-14 h-14 rounded-full object-cover"
                  />
                  <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-success rounded-full border-2 border-white flex items-center justify-center">
                    <Icon name="Check" size={12} className="text-white" />
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-text-primary">{testimonial.name}</h3>
                  <p className="text-sm text-text-secondary">{testimonial.role}</p>
                  <p className="text-xs text-text-muted">{testimonial.company}</p>
                </div>
              </div>

              {/* Rating */}
              <div className="flex items-center space-x-2 mb-4">
                <div className="flex space-x-1">
                  {renderStars(testimonial.rating)}
                </div>
                <span className="text-sm text-text-muted">({testimonial.rating}.0)</span>
              </div>

              {/* Content */}
              <blockquote className="text-text-secondary text-sm leading-relaxed mb-6">
                "{testimonial.content}"
              </blockquote>

              {/* Footer */}
              <div className="flex items-center justify-between pt-4 border-t border-border">
                <div>
                  <p className="text-xs font-medium text-text-primary">{testimonial.project}</p>
                  <p className="text-xs text-text-muted">Project Type</p>
                </div>
                <div className="inline-flex items-center space-x-1 bg-primary/10 text-primary px-3 py-1 rounded-full">
                  <Icon name="Award" size={12} />
                  <span className="text-xs font-medium">{testimonial.highlight}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="mt-16 text-center">
          <div className="bg-white rounded-2xl p-8 lg:p-12 shadow-lg max-w-3xl mx-auto">
            <Icon name="MessageCircle" size={48} className="text-primary mx-auto mb-6" />
            <h3 className="text-2xl font-bold text-text-primary mb-4">
              Ready to Start Your Project?
            </h3>
            <p className="text-text-secondary mb-8 leading-relaxed">
              Join the growing list of satisfied clients who've experienced seamless collaboration and exceptional results. Let's discuss how I can help bring your vision to life.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' })}
                className="inline-flex items-center justify-center px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors font-medium"
              >
                <Icon name="Send" size={20} className="mr-2" />
                Start a Conversation
              </button>
              <button
                onClick={() => window.open('https://www.linkedin.com/in/lahiru-senavirathna-39b11a215/', '_blank')}
                className="inline-flex items-center justify-center px-6 py-3 border border-border text-text-primary rounded-lg hover:bg-surface transition-colors font-medium"
              >
                <Icon name="Linkedin" size={20} className="mr-2" />
                View LinkedIn Profile
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;